import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export interface BannedWordDto {
  id: string;
  word: string;
  normalized: string;
  category: string | null;
  scope: 'global' | 'tenant';
  createdAt: string;
}

export interface CreateBannedWordInput {
  word: string;
  category?: string;
}

const QK = {
  tenant: ['banned-words', 'tenant'] as const,
  global: ['banned-words', 'global'] as const,
};

// ── Dizionario del tenant (CLUB_ADMIN) ───────────────────────────────────────

export function useBannedWords() {
  return useQuery({
    queryKey: QK.tenant,
    queryFn: async () => {
      const res = await apiClient.get<BannedWordDto[]>('/admin/banned-words');
      return res.data;
    },
  });
}

export function useCreateBannedWord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBannedWordInput) => {
      const res = await apiClient.post<BannedWordDto>('/admin/banned-words', input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.tenant });
    },
  });
}

export function useDeleteBannedWord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/banned-words/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.tenant });
    },
  });
}

// ── Dizionario globale (SUPER_ADMIN) ─────────────────────────────────────────

export function useGlobalBannedWords(enabled: boolean) {
  return useQuery({
    queryKey: QK.global,
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<BannedWordDto[]>('/superadmin/banned-words');
      return res.data;
    },
  });
}

export function useCreateGlobalBannedWord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBannedWordInput) => {
      const res = await apiClient.post<BannedWordDto>('/superadmin/banned-words', input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.global });
    },
  });
}

export function useDeleteGlobalBannedWord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/superadmin/banned-words/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.global });
    },
  });
}
